import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  defaultRule,
  modesFor,
  promptFor,
  rulePlain,
  withMode,
  type RuleMode,
  type VariantDraft,
  type VariantKind,
} from '@/lib/variantShape'
import type { ModifierGroup } from '@/types'
import { ChoicesGrid } from './ChoicesGrid'

/**
 * One variant list, edited in place (F-MOD-N12).
 *
 * The same component serves three hosts: a new question typed on the product
 * form, a shared list opened from a product, and a row on the Shared lists
 * page. Everything an owner needs in the first minute is on the main path:
 * name, kind, choices, and the rule as a sentence. Everything else (cross
 * list rules, repeat picks, ingredients) is under Advanced.
 */

const KIND_COPY: Record<VariantKind, { title: string; blurb: string }> = {
  size: { title: 'Sizes', blurb: 'Each choice is the item’s full price. Customer picks exactly one.' },
  one: { title: 'Pick one', blurb: 'One choice. Some choices can cost extra.' },
  many: { title: 'Extras', blurb: 'Any number of choices. Each adds to the price.' },
}

export function VariantListEditor({
  draft,
  onChange,
  otherGroups,
  currencyPrefix,
  lockKind,
  sharedCount,
  renderIngredients,
  ingredientsNote,
  compact,
}: {
  draft: VariantDraft
  onChange: (d: VariantDraft) => void
  /** Lists a choice may retune (cross-list rules). Never includes this one. */
  otherGroups: ModifierGroup[]
  currencyPrefix: string
  /** The product form's size list is always a size list; hide the kind picker. */
  lockKind?: boolean
  /** How many items this list is asked on, when more than one. */
  sharedCount?: number
  /** Shared-lists page mounts the ingredients panel per saved choice here. */
  renderIngredients?: (choice: { id: string; name: string }) => ReactNode
  /** Product form shows a pointer to Costing instead. */
  ingredientsNote?: ReactNode
  /** Skip the name/prompt row (the product form's size list names itself). */
  compact?: boolean
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const set = <K extends keyof VariantDraft>(k: K, v: VariantDraft[K]) => onChange({ ...draft, [k]: v })

  const setKind = (kind: VariantKind) => {
    if (kind === draft.kind) return
    onChange({
      ...draft,
      kind,
      rule: defaultRule(kind),
      prompt: draft.prompt === promptFor(draft.name, draft.kind) ? promptFor(draft.name, kind) : draft.prompt,
      // A single-pick list keeps at most one default.
      choices: kind === 'many' ? draft.choices : draft.choices.map((c, i) => ({ ...c, isDefault: c.isDefault && draft.choices.findIndex((x) => x.isDefault) === i })),
    })
  }

  const setName = (name: string) => {
    const autoPrompt = draft.prompt === '' || draft.prompt === promptFor(draft.name, draft.kind)
    onChange({ ...draft, name, prompt: autoPrompt ? promptFor(name, draft.kind) : draft.prompt })
  }

  const namedChoices = draft.choices.filter((c) => c.name.trim() !== '')
  const modes = modesFor(draft.kind)
  const r = draft.rule

  return (
    <div className="space-y-4">
      {sharedCount != null && sharedCount > 1 && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
          Shared list. Saving changes it on <strong>all {sharedCount} items</strong> that ask it.
        </p>
      )}

      {!compact && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`vl-name-${draft.id ?? 'new'}`}>Name</Label>
            <Input
              id={`vl-name-${draft.id ?? 'new'}`}
              value={draft.name}
              onChange={(e) => setName(e.target.value)}
              placeholder={draft.kind === 'size' ? 'Size' : draft.kind === 'many' ? 'Extras' : 'Milk'}
              autoFocus={!draft.id}
            />
            <p className="text-xs text-muted-foreground">For your menu screens.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`vl-prompt-${draft.id ?? 'new'}`}>What the cashier sees</Label>
            <Input
              id={`vl-prompt-${draft.id ?? 'new'}`}
              value={draft.prompt}
              onChange={(e) => set('prompt', e.target.value)}
              placeholder={promptFor(draft.name || 'milk', draft.kind) || 'Which milk?'}
            />
          </div>
        </div>
      )}

      {!lockKind && (
        <div className="grid gap-2 sm:grid-cols-3">
          {(['size', 'one', 'many'] as VariantKind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              aria-pressed={draft.kind === k}
              className={cn(
                'rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                draft.kind === k ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
              )}
            >
              <span className="block text-sm font-semibold">{KIND_COPY[k].title}</span>
              <span className={cn('block text-xs', draft.kind === k ? 'text-foreground' : 'text-muted-foreground')}>
                {KIND_COPY[k].blurb}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Label>Choices</Label>
          <span className="text-xs text-muted-foreground">
            {draft.kind === 'size' ? 'Each price is what the item sells for at that size.' : 'Prices are added on top. Blank = free.'}
          </span>
        </div>
        <ChoicesGrid kind={draft.kind} choices={draft.choices} onChange={(c) => set('choices', c)} currencyPrefix={currencyPrefix} />
      </div>

      <div className="space-y-1.5">
        <Label>Rule</Label>
        {draft.kind === 'size' ? (
          <p className="text-sm">
            Customer <strong>must pick exactly 1</strong>. A size always sets the price, so this cannot change.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Customer</span>
            <Select value={r.mode} onValueChange={(m) => set('rule', withMode(r, m as RuleMode))}>
              <SelectTrigger className="h-8 w-auto gap-1 border-primary/30 bg-primary/5 font-medium text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {modes.map((m) => (
                  <SelectItem key={m.mode} value={m.mode}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(r.mode === 'upto') && (
              <>
                <Input type="number" min={1} className="h-8 w-16 text-right tabular-nums" value={r.m ?? 1} aria-label="Maximum choices"
                  onChange={(e) => set('rule', { ...r, m: Math.max(1, Number(e.target.value) || 1) })} />
                <span>choices.</span>
              </>
            )}
            {(r.mode === 'atleast') && (
              <>
                <Input type="number" min={1} className="h-8 w-16 text-right tabular-nums" value={r.n} aria-label="Minimum choices"
                  onChange={(e) => set('rule', { ...r, n: Math.max(1, Number(e.target.value) || 1) })} />
                <span>choices.</span>
              </>
            )}
            {(r.mode === 'exactly' && draft.kind === 'many') && (
              <>
                <Input type="number" min={1} className="h-8 w-16 text-right tabular-nums" value={r.n} aria-label="Exact number of choices"
                  onChange={(e) => { const n = Math.max(1, Number(e.target.value) || 1); set('rule', { ...r, n, m: n }) }} />
                <span>choices.</span>
              </>
            )}
            {(r.mode === 'between') && (
              <>
                <Input type="number" min={1} className="h-8 w-16 text-right tabular-nums" value={r.n} aria-label="Minimum choices"
                  onChange={(e) => set('rule', { ...r, n: Math.max(1, Number(e.target.value) || 1) })} />
                <span>and</span>
                <Input type="number" min={1} className="h-8 w-16 text-right tabular-nums" value={r.m ?? r.n} aria-label="Maximum choices"
                  onChange={(e) => set('rule', { ...r, m: Math.max(1, Number(e.target.value) || 1) })} />
                <span>choices.</span>
              </>
            )}
            {(r.mode === 'optional' || r.mode === 'required' || r.mode === 'any') && <span>.</span>}
          </div>
        )}
        <p className="rounded-md border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
          {rulePlain(draft.kind, r, namedChoices.length)}
        </p>
      </div>

      {/* Advanced: folded by default. Everything here is real and used, but
          not by the owner setting up their first latte. */}
      <div className="rounded-lg border">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium"
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((o) => !o)}
        >
          Advanced
          {advancedOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </button>
        {advancedOpen && (
          <div className="space-y-4 border-t px-3 py-3 text-sm">
            {draft.kind === 'many' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <Checkbox checked={draft.allowQuantity} onCheckedChange={(c) => set('allowQuantity', Boolean(c))} />
                  Same choice more than once
                  <span className="text-xs text-muted-foreground">(the kitchen sees “Extra shot ×2”)</span>
                </label>
                {draft.allowQuantity && (
                  <div className="flex items-center gap-2 pl-6 text-xs">
                    <span>Most times one choice can be picked</span>
                    <Input type="number" min={1} className="h-8 w-16 text-right tabular-nums" value={draft.maxPerOption}
                      onChange={(e) => set('maxPerOption', Math.max(1, Number(e.target.value) || 1))} />
                  </div>
                )}
              </div>
            )}

            <CrossListRules draft={draft} onChange={onChange} otherGroups={otherGroups} />

            <div className="space-y-2">
              <p className="font-medium">Ingredients per choice</p>
              {renderIngredients && namedChoices.some((c) => c.id) ? (
                <div className="space-y-1.5">
                  {namedChoices.filter((c) => c.id).map((c) => (
                    <IngredientRow key={c.id} name={c.name}>{renderIngredients({ id: c.id as string, name: c.name })}</IngredientRow>
                  ))}
                  {namedChoices.some((c) => !c.id) && (
                    <p className="text-xs text-muted-foreground">New choices get ingredients after the first save.</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {ingredientsNote ?? 'What each choice adds to or replaces in the recipe. Save first, then set it under Costing & Recipes.'}
                </p>
              )}
            </div>

            {draft.id && (
              <label className="flex items-center gap-2">
                <Checkbox checked={draft.isActive} onCheckedChange={(c) => set('isActive', Boolean(c))} />
                Active
                <span className="text-xs text-muted-foreground">(off hides it from every till without deleting it)</span>
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function IngredientRow({ name, children }: { name: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-md border">
      <button type="button" className="flex w-full items-center justify-between px-2.5 py-1.5 text-xs" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="font-medium">{name}</span>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open && <div className="border-t p-2.5">{children}</div>}
    </div>
  )
}

/**
 * D5 rules as sentences: "When Large is picked, Scoops asks for 3." The data
 * lives on the option (modifier_option_group_rules); the sentence lists every
 * option's rules together so the owner reads the whole story in one place.
 */
function CrossListRules({
  draft,
  onChange,
  otherGroups,
}: {
  draft: VariantDraft
  onChange: (d: VariantDraft) => void
  otherGroups: ModifierGroup[]
}) {
  const named = draft.choices.map((c, i) => ({ c, i })).filter((x) => x.c.name.trim() !== '')
  const rows = named.flatMap(({ c, i }) => c.groupRules.map((rule, ri) => ({ i, ri, c, rule })))

  const setRule = (ci: number, ri: number, patch: Partial<{ target_group_id: string; min_select: number | null; max_select: number | null }>) =>
    onChange({
      ...draft,
      choices: draft.choices.map((c, j) =>
        j === ci ? { ...c, groupRules: c.groupRules.map((r, k) => (k === ri ? { ...r, ...patch } : r)) } : c,
      ),
    })
  const removeRule = (ci: number, ri: number) =>
    onChange({
      ...draft,
      choices: draft.choices.map((c, j) => (j === ci ? { ...c, groupRules: c.groupRules.filter((_, k) => k !== ri) } : c)),
    })
  const addRule = () => {
    const first = named[0]
    const target = otherGroups[0]
    if (!first || !target) return
    onChange({
      ...draft,
      choices: draft.choices.map((c, j) =>
        j === first.i ? { ...c, groupRules: [...c.groupRules, { target_group_id: target.id, min_select: 1, max_select: 1 }] } : c,
      ),
    })
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium">A choice changes another question</p>
          <p className="text-xs text-muted-foreground">Picking Large can make Scoops ask for 3 where Medium asks for 2.</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={addRule} disabled={named.length === 0 || otherGroups.length === 0}>
          Add rule
        </Button>
      </div>
      {otherGroups.length === 0 && <p className="text-xs text-muted-foreground">Needs another list to point at.</p>}
      {rows.map(({ i, ri, c, rule }) => (
        <div key={`${i}-${ri}`} className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 px-2 py-1.5 text-xs">
          <span>When</span>
          <Select value={String(i)} onValueChange={(v) => {
            // Move the rule to another choice.
            const to = Number(v)
            if (to === i) return
            const next = draft.choices.map((ch, j) => {
              if (j === i) return { ...ch, groupRules: ch.groupRules.filter((_, k) => k !== ri) }
              if (j === to) return { ...ch, groupRules: [...ch.groupRules, rule] }
              return ch
            })
            onChange({ ...draft, choices: next })
          }}>
            <SelectTrigger className="h-7 w-auto"><SelectValue /></SelectTrigger>
            <SelectContent>{named.map((n) => <SelectItem key={n.i} value={String(n.i)}>{n.c.name}</SelectItem>)}</SelectContent>
          </Select>
          <span>is picked,</span>
          <Select value={rule.target_group_id} onValueChange={(v) => setRule(i, ri, { target_group_id: v })}>
            <SelectTrigger className="h-7 w-auto"><SelectValue /></SelectTrigger>
            <SelectContent>{otherGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
          <span>asks for</span>
          <Input type="number" min={0} className="h-7 w-14 text-right tabular-nums" aria-label="Minimum" value={rule.min_select ?? ''} placeholder="min"
            onChange={(e) => setRule(i, ri, { min_select: e.target.value === '' ? null : Number(e.target.value) })} />
          <span>to</span>
          <Input type="number" min={1} className="h-7 w-14 text-right tabular-nums" aria-label="Maximum" value={rule.max_select ?? ''} placeholder="max"
            onChange={(e) => setRule(i, ri, { max_select: e.target.value === '' ? null : Number(e.target.value) })} />
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" aria-label="Remove rule" onClick={() => removeRule(i, ri)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          <span className="w-full text-[11px] text-muted-foreground sm:w-auto">{c.name} → {otherGroups.find((g) => g.id === rule.target_group_id)?.name ?? '?'}</span>
        </div>
      ))}
    </div>
  )
}
